<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] mx-auto overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <div>
            <h1 class="text-xl font-bold text-white">File Download</h1>
            <p class="text-red-100 text-sm">Selectair Admin</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="px-8 py-8">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
          <svg class="animate-spin h-8 w-8 text-red-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-gray-600">Loading file information...</p>
        </div>

        <!-- File Not Found -->
        <div v-else-if="error" class="text-center py-8">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">File Not Found</h3>
          <p class="text-gray-600">{{ error }}</p>
        </div>

        <!-- File Info and Password Entry -->
        <div v-else-if="file" class="space-y-6">
          <!-- File Information -->
          <div class="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
            <div class="w-16 h-16 rounded-xl flex items-center justify-center" :class="getFileTypeColor(file.original_name, file.mime_type)">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-html="getFileTypeIcon(file.original_name, file.mime_type)"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-semibold text-gray-900 truncate">{{ file.original_name }}</h3>
              <div class="flex items-center space-x-4 text-sm text-gray-500">
                <span class="flex items-center space-x-1">
                  <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
                  </svg>
                  <span>{{ formatFileSize(file.file_size) }}</span>
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                  {{ getFileTypeLabel(file.original_name, file.mime_type) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Public File - Direct Download -->
          <div v-if="file.is_public" class="text-center space-y-4">
            <div class="flex items-center justify-center p-4 bg-green-50 rounded-xl">
              <svg class="w-8 h-8 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
              </svg>
              <span class="text-green-700 font-medium">This file is publicly accessible</span>
            </div>
            <button 
              @click="downloadFile"
              :disabled="downloading"
              class="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              <svg v-if="downloading" class="animate-spin w-8 h-8 mr-2 inline" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-8 h-8 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span>{{ downloading ? 'Downloading...' : 'Download File' }}</span>
            </button>
          </div>

          <!-- Private File - Password Required -->
          <div v-else class="space-y-4">
            <div class="flex items-center justify-center p-4 bg-yellow-50 rounded-xl">
              <svg class="w-8 h-8 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              <span class="text-yellow-700 font-medium">This file is password-protected</span>
            </div>

            <!-- Password Input Form -->
            <form @submit.prevent="downloadWithPassword" class="space-y-4">
              <div>
                <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                  Enter Password
                </label>
                <div class="relative">
                  <input
                    id="password"
                    type="password"
                    v-model="password"
                    required
                    class="w-full px-4 py-3 pl-12 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                    placeholder="Enter the file password"
                    :class="{ 'border-red-300 bg-red-50': passwordError }"
                  />
                  <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2H7v-2H4a1 1 0 01-1-1v-4a1 1 0 011-1h3l4.586-4.586A6 6 0 0121 9z"></path>
                    </svg>
                  </div>
                </div>
                <p v-if="passwordError" class="mt-2 text-sm text-red-600">{{ passwordError }}</p>
              </div>

              <button 
                type="submit"
                :disabled="downloading || !password.trim()"
                class="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:transform-none"
              >
                <svg v-if="downloading" class="animate-spin w-8 h-8 mr-2 inline" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-8 h-8 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>{{ downloading ? 'Downloading...' : 'Download File' }}</span>
              </button>
            </form>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-8 pb-6">
        <div class="text-center text-sm text-gray-500">
          <p>© 2025 Selectair Group. All rights reserved.</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { getFileType, getFileColor, fileIcons } from '@/utils/fileTypes'

export default {
  name: 'FileDownload',
  data() {
    return {
      file: null,
      loading: true,
      error: null,
      password: '',
      passwordError: '',
      downloading: false
    }
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
    }
  },
  async mounted() {
    await this.loadFile()
    
    if (this.file) {
      // Check URL parameters for behavior control
      const { password: urlPassword, admin, autodownload } = this.$route.query
      
      if (this.file.is_public) {
        // Auto-download public files (unless explicitly disabled)
        if (autodownload !== 'false') {
          await this.downloadFile()
        }
      } else if (admin === 'true') {
        // Admin mode - use JWT authentication
        await this.downloadFile()
      } else if (urlPassword) {
        // Password provided in URL - auto-download with password
        this.password = urlPassword
        await this.downloadWithPassword()
      }
      // Otherwise, show password form for private files
    }
  },
  methods: {
    async loadFile() {
      try {
        this.loading = true
        const fileId = this.$route.params.id
        
        const response = await axios.get(`${this.apiUrl}/api/admin/files/${fileId}/info`)
        
        if (response.data.success) {
          this.file = response.data.data
        } else {
          this.error = response.data.message || 'File not found'
        }
      } catch (error) {
        console.error('Error loading file:', error)
        this.error = 'Failed to load file information'
      } finally {
        this.loading = false
      }
    },

    async downloadFile() {
      try {
        this.downloading = true
        const fileId = this.$route.params.id
        
        // For public files, try direct download via redirect first
        if (this.file && this.file.is_public) {
          // Use a simple window.open approach for public files to handle redirects
          const downloadUrl = `${this.apiUrl}/api/admin/files/${fileId}/download`
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = this.file.original_name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          link.remove()
          
          this.$root.$toast.success('Your file download has begun successfully.', 'Download Started')
          return
        }
        
        // For private files, use blob method with authentication
        // Only send JWT token if admin=true in URL (admin mode)
        const isAdminMode = this.$route.query.admin === 'true'
        const token = localStorage.getItem('admin_token')
        const headers = (isAdminMode && token) ? { Authorization: `Bearer ${token}` } : {}
        
        const response = await axios.get(`${this.apiUrl}/api/admin/files/${fileId}/download`, {
          headers,
          responseType: 'blob'
        })
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.file.original_name)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        this.$root.$toast.success('Your file download has begun successfully.', 'Download Started')
      } catch (error) {
        console.error('Error downloading file:', error)
        this.$root.$toast.error('Download failed. Please try again.', 'Download Error')
      } finally {
        this.downloading = false
      }
    },

    async downloadWithPassword() {
      try {
        this.downloading = true
        this.passwordError = ''
        const fileId = this.$route.params.id
        
        // For password-protected downloads, DO NOT send JWT token
        // This ensures we test the password, not admin authentication
        const response = await axios.get(`${this.apiUrl}/api/admin/files/${fileId}/download`, {
          params: { password: this.password },
          responseType: 'blob'
        })
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.file.original_name)
        document.body.appendChild(link)
        link.click()
        link.remove()
        
        this.$root.$toast.success('Your file download has begun successfully.', 'Download Started')
      } catch (error) {
        console.error('Error downloading file:', error)
        if (error.response?.status === 403) {
          this.passwordError = 'Incorrect password. Please try again.'
        } else {
          this.passwordError = 'Download failed. Please try again.'
        }
      } finally {
        this.downloading = false
      }
    },

    getFileTypeColor(filename, mimeType) {
      return getFileColor(filename, mimeType)
    },

    getFileTypeIcon(filename, mimeType) {
      const fileInfo = getFileType(filename, mimeType)
      return fileIcons[fileInfo.icon] || fileIcons.document
    },

    getFileTypeLabel(filename, mimeType) {
      const fileInfo = getFileType(filename, mimeType)
      return fileInfo.type.toUpperCase()
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.transform {
  animation: fadeIn 0.3s ease-out;
}
</style>
